<template>
  <section class="ap-card">
    <div class="ap-card-head">
      <div class="ap-card-heading">
        <span class="ap-card-no">{{ number }}</span>
        <div>
          <h2 class="ap-card-title">Animals</h2>
          <p class="ap-card-sub">Tell us whether any animals will appear in the shoot.</p>
        </div>
      </div>
    </div>

    <div class="ap-card-body">
      <!-- Yes / No -->
      <div class="an-choice" role="radiogroup" aria-label="Are animals involved in the shoot?">
        <button
          type="button"
          role="radio"
          class="an-option"
          :class="{ 'an-option--active': involved === 'NO' }"
          :aria-checked="involved === 'NO' ? 'true' : 'false'"
          @click="choose('NO')"
        >
          <span class="an-option-icon"><v-icon>mdi-leaf</v-icon></span>
          <span class="an-option-text">
            <span class="an-option-title">No animals</span>
            <span class="an-option-sub">The shoot doesn't involve any animals.</span>
          </span>
          <span class="an-option-check"><v-icon small>mdi-check</v-icon></span>
        </button>
        <button
          type="button"
          role="radio"
          class="an-option"
          :class="{ 'an-option--active': involved === 'YES' }"
          :aria-checked="involved === 'YES' ? 'true' : 'false'"
          @click="choose('YES')"
        >
          <span class="an-option-icon"><v-icon>mdi-paw</v-icon></span>
          <span class="an-option-text">
            <span class="an-option-title">Yes, animals are involved</span>
            <span class="an-option-sub">List them and attach the pre-shoot order.</span>
          </span>
          <span class="an-option-check"><v-icon small>mdi-check</v-icon></span>
        </button>
      </div>

      <template v-if="involved === 'YES'">
        <!-- A. Animals -->
        <div class="an-step">
          <span class="an-step-letter">A</span>
          <div class="an-step-text">
            <div class="an-step-title">Animals in the shoot</div>
            <div class="an-step-sub">Add each species and how many will be on set.</div>
          </div>
          <span class="an-summary">
            <v-icon x-small>mdi-paw</v-icon>
            {{ speciesCount }} species · {{ total }} animal{{ total === 1 ? "" : "s" }}
          </span>
        </div>

        <div class="an-list">
          <div v-for="(a, i) in animals" :key="i" class="an-row">
            <span class="an-row-no">{{ i + 1 }}</span>
            <div class="an-species">
              <v-autocomplete
                :value="a.choice"
                :items="speciesOptions"
                outlined
                dense
                hide-details
                label="Species *"
                placeholder="Choose a species"
                class="an-species-pick"
                @change="setChoice(a, $event)"
              ></v-autocomplete>
              <v-text-field
                v-if="a.choice === OTHER"
                v-model="a.species"
                outlined
                dense
                hide-details
                autofocus
                label="Type the species name *"
                placeholder="e.g. Peacock"
                prepend-inner-icon="mdi-pencil-outline"
                class="an-species-other"
              ></v-text-field>
            </div>
            <div class="an-stepper" :aria-label="'Number of ' + (a.species || 'animals')">
              <button type="button" class="an-step-btn" :disabled="Number(a.count) <= 1" aria-label="Fewer" @click="a.count = Math.max(1, Number(a.count) - 1)">
                <v-icon small>mdi-minus</v-icon>
              </button>
              <input v-model.number="a.count" type="number" min="1" class="an-count" aria-label="Count" />
              <button type="button" class="an-step-btn" aria-label="More" @click="a.count = Number(a.count || 0) + 1">
                <v-icon small>mdi-plus</v-icon>
              </button>
            </div>
            <button v-if="animals.length > 1" type="button" class="an-remove" :aria-label="'Remove ' + (a.species || 'animal')" @click="animals.splice(i, 1)">
              <v-icon small>mdi-close</v-icon>
            </button>
          </div>
          <button type="button" class="an-add" @click="addAnimal">
            <v-icon small>mdi-plus</v-icon>
            Add another animal
          </button>
        </div>

        <!-- B. Pre-shoot order -->
        <div class="an-step">
          <span class="an-step-letter">B</span>
          <div class="an-step-text">
            <div class="an-step-title">Pre-shoot permission</div>
            <div class="an-step-sub">One pre-shoot order covers all the animals listed above.</div>
          </div>
        </div>

        <div class="ap-grid">
          <v-text-field
            :value="preshootNumber"
            outlined
            dense
            hide-details
            label="Pre-shoot order number *"
            prepend-inner-icon="mdi-file-certificate-outline"
            @input="$emit('update:preshootNumber', $event)"
          ></v-text-field>
          <v-menu v-model="fromMenu" :close-on-content-click="false" max-width="290">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="display(validityFrom)"
                outlined
                dense
                readonly
                clearable
                hide-details
                label="Valid from *"
                prepend-inner-icon="mdi-calendar-start"
                v-bind="attrs"
                v-on="on"
                @click:clear="$emit('update:validityFrom', null)"
              ></v-text-field>
            </template>
            <v-date-picker :value="validityFrom" :min="minDate" color="#16a34a" @change="$emit('update:validityFrom', $event); fromMenu = false"></v-date-picker>
          </v-menu>
          <v-menu v-model="toMenu" :close-on-content-click="false" max-width="290">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                :value="display(validityTo)"
                outlined
                dense
                readonly
                clearable
                hide-details
                label="Valid to *"
                prepend-inner-icon="mdi-calendar-end"
                v-bind="attrs"
                v-on="on"
                @click:clear="$emit('update:validityTo', null)"
              ></v-text-field>
            </template>
            <v-date-picker :value="validityTo" :min="validityFrom || minDate" color="#16a34a" @change="$emit('update:validityTo', $event); toMenu = false"></v-date-picker>
          </v-menu>
        </div>

        <div class="an-file-row">
          <label class="ap-file" :class="{ 'ap-file--done': !!fileName }">
            <span class="ap-file-icon"><v-icon>{{ fileName ? "mdi-check" : "mdi-paperclip" }}</v-icon></span>
            <span class="ap-file-text">
              <span class="ap-file-title">Pre-shoot order copy <span class="ap-req">*</span></span>
              <span class="ap-file-hint">{{ fileName || (existingUrl ? "Choose a file to replace the current one" : "PDF, JPG or PNG, up to 1 MB") }}</span>
            </span>
            <input type="file" accept=".pdf, .jpeg, .jpg, .png" @change="onFile" />
          </label>
          <a v-if="existingUrl" :href="existingUrl" target="_blank" rel="noopener" class="ap-btn ap-btn--outline ap-btn--sm">
            <v-icon small>mdi-eye-outline</v-icon>
            View current
          </a>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
// Animals section of the application form and the edit form. The parent
// owns the data: `animals` is an array of { species, count } edited in place;
// the other values use .sync. The chosen pre-shoot order file is emitted as
// "file" so the parent can validate and store it.
//
// Each row also gets a `choice`: the picked list entry, or OTHER when the
// applicant types a species that is not in the list. Only species and count
// are sent to the backend.
const OTHER = "__other__";

export default {
  name: "AnimalsSection",
  props: {
    number: { type: [Number, String], default: 4 },
    involved: { type: String, default: "" },
    animals: { type: Array, required: true },
    preshootNumber: { type: [String, Number], default: "" },
    validityFrom: { type: String, default: null },
    validityTo: { type: String, default: null },
    minDate: { type: String, default: null },
    fileName: { type: String, default: "" },
    existingUrl: { type: String, default: "" },
  },
  data() {
    return {
      OTHER,
      fromMenu: false,
      toMenu: false,
      suggestions: ["Elephant", "Horse", "Dog", "Cat", "Cow", "Buffalo", "Goat", "Sheep", "Camel", "Monkey", "Snake", "Parrot", "Pigeon", "Rabbit", "Hen"],
    };
  },
  watch: {
    // Rows loaded from a saved application have no `choice` yet.
    animals: {
      immediate: true,
      handler(rows) {
        rows.forEach((row) => {
          if (row.choice !== undefined) return;
          let choice = null;
          if (row.species) choice = this.suggestions.includes(row.species) ? row.species : OTHER;
          this.$set(row, "choice", choice);
        });
      },
    },
  },
  computed: {
    speciesOptions() {
      return this.suggestions
        .map((name) => ({ text: name, value: name }))
        .concat([{ divider: true }, { text: "Other (type the name)", value: OTHER }]);
    },
    filled() {
      return this.animals.filter((a) => a.species && String(a.species).trim());
    },
    speciesCount() {
      return this.filled.length;
    },
    total() {
      return this.filled.reduce((sum, a) => sum + (Number(a.count) || 0), 0);
    },
  },
  methods: {
    choose(value) {
      this.$emit("update:involved", value);
      if (value === "YES" && !this.animals.length) this.addAnimal();
    },
    addAnimal() {
      this.animals.push({ species: "", count: 1, choice: null });
    },
    // A listed species fills the name; "Other" clears it for typing.
    setChoice(row, value) {
      this.$set(row, "choice", value || null);
      row.species = value && value !== OTHER ? value : "";
    },
    onFile(event) {
      const file = event.target.files && event.target.files[0];
      if (file) this.$emit("file", file);
      event.target.value = "";
    },
    display(date) {
      if (!date) return "";
      const [y, m, d] = String(date).substr(0, 10).split("-");
      return d + "-" + m + "-" + y;
    },
  },
};
</script>

<style scoped>
.an-choice {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.an-option {
  position: relative;
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 18px;
  border: 1.5px solid var(--ap-line);
  background: #ffffff;
  text-align: left;
  font-family: inherit;
  cursor: pointer;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.an-option:hover {
  border-color: rgba(22, 163, 74, 0.4);
}

.an-option--active {
  border-color: #16a34a;
  background: linear-gradient(135deg, #f0fdf4 0%, #ffffff 70%);
  box-shadow: 0 10px 24px rgba(22, 163, 74, 0.12);
}

.an-option-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  flex-shrink: 0;
  border-radius: 14px;
  background: var(--ap-emerald-tint);
}

.an-option-icon .v-icon {
  color: #15803d !important;
}

.an-option--active .an-option-icon {
  background: #16a34a;
}

.an-option--active .an-option-icon .v-icon {
  color: #ffffff !important;
}

.an-option-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.an-option-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ap-ink);
}

.an-option-sub {
  font-size: 12.5px;
  color: var(--ap-muted);
}

.an-option-check {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1.5px solid var(--ap-line);
}

.an-option-check .v-icon {
  color: transparent !important;
}

.an-option--active .an-option-check {
  border-color: #16a34a;
  background: #16a34a;
}

.an-option--active .an-option-check .v-icon {
  color: #ffffff !important;
}

.an-step {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin: 26px 0 14px;
}

.an-step-letter {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border-radius: 50%;
  background: var(--ap-forest);
  color: var(--ap-gold-soft);
  font-family: "Inter", sans-serif;
  font-size: 15px;
}

.an-step-text {
  flex: 1;
  min-width: 200px;
}

.an-step-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--ap-ink);
}

.an-step-sub {
  font-size: 12.5px;
  color: var(--ap-muted);
}

.an-summary {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 999px;
  background: var(--ap-emerald-tint);
  color: #14532d;
  font-size: 12.5px;
  font-weight: 600;
}

.an-summary .v-icon {
  color: inherit !important;
}

.an-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.an-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 16px;
  border: 1px solid var(--ap-line);
  background: #fbfdfc;
}

.an-row-no {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #ffffff;
  border: 1px solid var(--ap-line);
  font-size: 12.5px;
  font-weight: 700;
  color: var(--ap-gold);
}

.an-species {
  display: flex;
  flex: 1;
  gap: 10px;
  min-width: 0;
}

.an-species-pick,
.an-species-other {
  flex: 1 1 0;
  min-width: 0;
}

.an-stepper {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  border-radius: 12px;
  border: 1px solid #dbe5df;
  background: #ffffff;
  overflow: hidden;
}

.an-step-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 40px;
  border: none;
  background: none;
  cursor: pointer;
}

.an-step-btn .v-icon {
  color: #14532d !important;
}

.an-step-btn:hover:not(:disabled) {
  background: #f0fdf4;
}

.an-step-btn:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.an-count {
  width: 52px;
  height: 40px;
  border: none;
  border-left: 1px solid #e4ece7;
  border-right: 1px solid #e4ece7;
  outline: none;
  text-align: center;
  font-family: inherit;
  font-size: 15px;
  font-weight: 700;
  color: var(--ap-ink);
  -moz-appearance: textfield;
}

.an-count::-webkit-outer-spin-button,
.an-count::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.an-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid #fecaca;
  background: #ffffff;
  cursor: pointer;
}

.an-remove .v-icon {
  color: #b91c1c !important;
}

.an-remove:hover {
  background: #fef2f2;
}

.an-add {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 12px;
  border-radius: 16px;
  border: 1.5px dashed #b7d3c2;
  background: transparent;
  color: #14532d;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}

.an-add .v-icon {
  color: inherit !important;
}

.an-add:hover {
  border-color: #16a34a;
  background: #f4fbf7;
}

.an-file-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
}

.an-file-row .ap-file {
  flex: 1 1 320px;
}

@media (max-width: 700px) {
  .an-choice {
    grid-template-columns: 1fr;
  }

  .an-row {
    flex-wrap: wrap;
  }

  .an-species {
    flex: 1 1 100%;
    flex-direction: column;
  }
}
</style>
